import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';

const AutoLoginPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const autoLoginAndRedirect = async () => {
      try {
        // Get the selected plan from location state
        const selectedPlan = location.state?.selectedPlan;
        
        if (!selectedPlan) {
          setError('No plan selected. Please go back and select a plan.');
          setLoading(false);
          return;
        }

        // Check if user is already logged in
        const token = sessionStorage.getItem('authToken');
        const userEmail = sessionStorage.getItem('userEmail');

        if (token && userEmail) {
          // User is already logged in, redirect to payment
          console.log('✅ User already logged in, redirecting to payment');
          navigate('/payment', { state: { plan: selectedPlan } });
          return;
        }

        // For demo - auto login with a test user
        // In real app, you might have a different auto-login mechanism
        const testEmail = 'demo@recruit.com';
        
        console.log('🔄 Auto-login initiated for:', testEmail);

        // Send OTP for auto-login
        const otpResponse = await axios.post(`${API_BASE_URL}/api/hiring/send-otp`, {
          email: testEmail,
          purpose: 'login'
        });

        if (otpResponse.data.success) {
          // Auto-verify with a demo OTP (in real app, this would be different)
          const verifyResponse = await axios.post(`${API_BASE_URL}/api/hiring/verify-otp`, {
            email: testEmail,
            otp: '123456' // Demo OTP - in real app use actual OTP
          });

          if (verifyResponse.data.success) {
            // Store user data
            sessionStorage.setItem('authToken', verifyResponse.data.token);
            sessionStorage.setItem('userData', JSON.stringify(verifyResponse.data.data));
            sessionStorage.setItem('userEmail', testEmail);

            console.log('✅ Auto-login successful, redirecting to payment');
            navigate('/payment', { state: { plan: selectedPlan } });
          } else {
            setError('Auto-login failed. Please try manual login.');
            setLoading(false);
          }
        } else {
          setError('Failed to initiate auto-login. Please try manual login.');
          setLoading(false);
        }

      } catch (error) {
        console.error('Auto-login error:', error);
        setError('Auto-login failed. Please try manual login.');
        setLoading(false);
      }
    };

    autoLoginAndRedirect();
  }, [navigate, location]);

  const handleManualLogin = () => {
    const selectedPlan = location.state?.selectedPlan;
    if (selectedPlan) {
      sessionStorage.setItem('selectedPlanAfterLogin', JSON.stringify(selectedPlan));
      sessionStorage.setItem('redirectAfterLogin', '/payment');
    }
    navigate('/access-recruit');
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingContent}>
          <div style={styles.spinner}></div>
          <p>Setting up your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.errorContainer}>
      <div style={styles.errorContent}>
        <div style={styles.errorIcon}>⚠️</div>
        <h2 style={styles.errorTitle}>Auto-Login Failed</h2>
        <p style={styles.errorMessage}>{error}</p>
        <button 
          onClick={handleManualLogin}
          style={styles.manualLoginButton}
        >
          Continue with Manual Login
        </button>
      </div>
    </div>
  );
};

const styles = {
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  loadingContent: {
    background: 'white',
    padding: '40px',
    borderRadius: '16px',
    textAlign: 'center',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
    maxWidth: '300px',
    width: '90%'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px'
  },
  errorContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  errorContent: {
    background: 'white',
    padding: '40px',
    borderRadius: '16px',
    textAlign: 'center',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
    maxWidth: '400px',
    width: '90%'
  },
  errorIcon: {
    fontSize: '4rem',
    marginBottom: '20px'
  },
  errorTitle: {
    color: '#d63031',
    marginBottom: '16px',
    fontSize: '1.8rem'
  },
  errorMessage: {
    color: '#666',
    marginBottom: '30px',
    fontSize: '1.1rem',
    lineHeight: '1.5'
  },
  manualLoginButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    padding: '16px 32px',
    borderRadius: '8px',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    transition: 'all 0.3s ease'
  }
};

export default AutoLoginPage;